import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { applyTransaction, withTransaction } from '@datorama/akita';
import { MarketValuesStore } from './market-values.store';
import { Cacheable } from 'ts-cacheable';
import { Instrument } from '../../../shared/state/instruments/instrument.model';
import { Currency } from '../../../shared/components/currency-selector/currency-country-codes';
import { catchError, map } from 'rxjs/operators';
import { Acct } from '../../../shared/state/accts/acct.model';
import { LegalEntity } from '../../../pages/admin/state/legal-entities/legal-entity.model';
import { MarketValue } from './market-value.model';
import { AppConfigQuery } from '../../../core/state/app-config/app-config.query';
import { transformData } from './market-values.utils';

@Injectable({ providedIn: 'root' })
export class MarketValuesService {
    constructor(
        private _marketValuesStore: MarketValuesStore,
        private _http: HttpClient,
        private _appConfig: AppConfigQuery
    ) {}
    /**
     * Like v1 except it's a POST request and the body is the params
     * @param accountIds - Array of account ids to filter by
     * @param legalEntityIds - Array of legal entity ids to filter by
     * @param instrumentIds - Array of instrument ids to filter by
     * @param baseCurrency - Base currency to filter by
     * @param fromDate - Start date to filter by
     * @param toDate - End date to filter by
     * @returns Observable<Array<MarketValue>> - Observable with market values
     */
    @Cacheable({
        maxCacheCount: 1
    })
    getV2({
        accountIds,
        legalEntityIds,
        instrumentIds,
        baseCurrency,
        fromDate,
        toDate
    }: {
        accountIds?: Array<Acct['accountId']>;
        instrumentIds?: Array<Instrument['instrumentId']>;
        legalEntityIds?: Array<LegalEntity['ownerId']>;
        fromDate?: Date;
        toDate?: Date;
        baseCurrency?: Currency;
    }) {
        let params: any = {
            startDate: '1970-01-01',
            endDate: new Date().toISOString().substring(0, 10)
        };
        if (fromDate && toDate) {
            params = {
                ...params,
                startDate: fromDate.toISOString().substring(0, 10),
                endDate: toDate.toISOString().substring(0, 10)
            };
        }
        if (baseCurrency) {
            params = {
                ...params,
                baseCurrency
            };
        }
        if (accountIds && accountIds.length > 0) {
            params = {
                ...params,
                accountIds: accountIds.join(',')
            };
        }
        if (instrumentIds && instrumentIds.length > 0) {
            params = {
                ...params,
                instrumentIds: instrumentIds.join(',')
            };
        }
        if (legalEntityIds && legalEntityIds.length > 0) {
            params = {
                ...params,
                legalEntityIds: legalEntityIds.join(',')
            };
        }
        return this._http
            .post<MarketValue[]>(
                `${this._appConfig.get().clickhouseBaseUrl}/v2/aggregates/totals`,
                params
            )
            .pipe(
                withTransaction((entities) => {
                    this._marketValuesStore.set(entities);
                    this._marketValuesStore.setLoading(false);
                }),
                catchError((error) => {
                    applyTransaction(() => {
                        this._marketValuesStore.setLoading(false);
                        this._marketValuesStore.setError(error);
                    });
                    throw error;
                })
            );
    }
    /**
     * @deprecated May be removed at any time in favor of getV2 - Use getV2 instead
     *
     * @param accountIds - Array of account ids to filter by
     * @param legalEntityIds - Array of legal entity ids to filter by
     * @param instrumentIds - Array of instrument ids to filter by
     * @param baseCurrency - Base currency to filter by
     * @param fromDate - Start date to filter by
     * @param toDate - End date to filter by
     * @returns Observable<Array<MarketValue>> - Observable with market values
     */
    get({
        accountIds,
        legalEntityIds,
        instrumentIds,
        baseCurrency,
        fromDate,
        toDate
    }: {
        accountIds?: Array<Acct['accountId']>;
        instrumentIds?: Array<Instrument['instrumentId']>;
        legalEntityIds?: Array<LegalEntity['ownerId']>;
        fromDate?: Date;
        toDate?: Date;
        baseCurrency?: Currency;
    }) {
        let params: any = {
            startDate: '1970-01-01',
            endDate: new Date().toISOString().substring(0, 10)
        };
        if (fromDate && toDate) {
            params = {
                ...params,
                startDate: fromDate.toISOString().substring(0, 10),
                endDate: toDate.toISOString().substring(0, 10)
            };
        }
        if (baseCurrency) {
            params = {
                ...params,
                baseCurrency
            };
        }
        if (accountIds && accountIds.length > 0) {
            params = {
                ...params,
                accountIds: accountIds.join(',')
            };
        }
        if (instrumentIds && instrumentIds.length > 0) {
            params = {
                ...params,
                instrumentIds: instrumentIds.join(',')
            };
        }
        if (legalEntityIds && legalEntityIds.length > 0) {
            params = {
                ...params,
                legalEntityIds: legalEntityIds.join(',')
            };
        }
        applyTransaction(() => {
            this._marketValuesStore.setError(null);
            this._marketValuesStore.setLoading(true);
        });
        return this._http
            .get<MarketValue[]>(
                `${this._appConfig.get().clickhouseBaseUrl}/v1/aggregates/totals`,
                {
                    params
                }
            )
            .pipe(
                withTransaction((entities) => {
                    this._marketValuesStore.set(entities);
                    this._marketValuesStore.setLoading(false);
                }),
                catchError((error) => {
                    applyTransaction(() => {
                        this._marketValuesStore.setLoading(false);
                        this._marketValuesStore.setError(error);
                    });
                    throw error;
                })
            );
    }
}
